import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { getCustomerById, updateCustomer, checkEmailUnique } from '../services/customer.service';
import { getCustomerCrops, addCustomerCrop, removeCustomerCrop } from '../services/customerCrop.service';
import { getAllCropTypes } from '../services/cropType.service';
import { getAllRegions } from '../services/region.service';
import { toastSuccess } from '../components/core';
import { validateCustomerForm, type CustomerFormFields, type CustomerFormErrors } from '../utils/validation';
import { Navbar } from '../components/Navbar';
import { BackButton, Button, Card, Input, Spinner, CustomerCropsTable, type CropRow } from '../components/core';

export default function EditCustomerPage() {
  const { user, signOut } = useAuth();
  const { org, orgId, orgSlug } = useOrg();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [fields, setFields] = useState<CustomerFormFields>({
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    email: '',
    registrationDate: '',
  });
  const [errors, setErrors] = useState<CustomerFormErrors>({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [cropRows, setCropRows] = useState<CropRow[]>([]);
  const [originalCropIds, setOriginalCropIds] = useState<Set<string>>(new Set());
  const [cropError, setCropError] = useState<string | undefined>(undefined);
  const [cropTypes, setCropTypes] = useState<{ id: string; name: string }[]>([]);
  const [regions, setRegions] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    if (!id || !orgId) return;

    const load = async () => {
      setLoading(true);
      try {
        // Customer is critical — failure blocks the page
        const customer = await getCustomerById(orgId, id);

        if (!customer || customer.isDeleted) {
          setNotFound(true);
          return;
        }

        setFields({
          firstName: customer.firstName,
          lastName: customer.lastName,
          phone: customer.phone,
          address: customer.address ?? '',
          email: customer.email ?? '',
          registrationDate: customer.registrationDate ?? '',
        });

        // Secondary data — load in parallel, fail gracefully
        const [cropsResult, cropTypesResult, regionsResult] = await Promise.allSettled([
          getCustomerCrops(orgId, id),
          getAllCropTypes(orgId),
          getAllRegions(orgId),
        ]);

        if (cropsResult.status === 'fulfilled') {
          setCropRows(cropsResult.value.map(c => ({ cropTypeId: c.cropTypeId, regionId: c.regionId })));
          setOriginalCropIds(new Set(cropsResult.value.map(c => c.id)));
        }
        if (cropTypesResult.status === 'fulfilled') setCropTypes(cropTypesResult.value);
        if (regionsResult.status === 'fulfilled') setRegions(regionsResult.value);
      } catch (err) {
        console.error('[EditCustomerPage] load error:', err);
        setSubmitError(t('form.loadError'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, orgId, t]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setFields(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof CustomerFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  }

  function handleCropChange(rows: CropRow[]) {
    setCropRows(rows);
    if (cropError) setCropError(undefined);
  }

  function validateCrops(rows: CropRow[]): boolean {
    const completeRows = rows.filter(r => r.cropTypeId && r.regionId);
    if (completeRows.length === 0) {
      setCropError(t('validation.atLeastOneCrop'));
      return false;
    }
    const keys = completeRows.map(r => `${r.cropTypeId}_${r.regionId}`);
    const hasDups = keys.length !== new Set(keys).size;
    if (hasDups) {
      setCropError(t('validation.duplicateCropRow'));
      return false;
    }
    return true;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const fieldErrors = validateCustomerForm(fields, t);
    const cropsValid = validateCrops(cropRows);

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
    }
    if (!cropsValid || Object.keys(fieldErrors).length > 0) {
      return;
    }

    if (!id || !orgId) return;
    setSaving(true);
    setSubmitError(null);

    try {
      if (fields.email.trim()) {
        const isUnique = await checkEmailUnique(orgId, fields.email.trim(), id);
        if (!isUnique) {
          setErrors(prev => ({ ...prev, email: t('validation.emailTaken') }));
          return;
        }
      }

      await updateCustomer(orgId, id, {
        firstName: fields.firstName.trim(),
        lastName: fields.lastName.trim(),
        phone: fields.phone.trim(),
        address: fields.address.trim(),
        email: fields.email.trim() || null,
        registrationDate: fields.registrationDate || null,
      });

      // Diff crop rows: add new ones, remove deleted ones
      const completeRows = cropRows.filter(r => r.cropTypeId && r.regionId);
      const newCropIds = new Set(completeRows.map(r => `${r.cropTypeId}_${r.regionId}`));

      const toAdd = completeRows.filter(r => !originalCropIds.has(`${r.cropTypeId}_${r.regionId}`));
      const toRemove = [...originalCropIds].filter(cropId => !newCropIds.has(cropId));

      await Promise.all([
        ...toAdd.map(row => addCustomerCrop(orgId, id, row)),
        ...toRemove.map(cropId => removeCustomerCrop(orgId, id, cropId)),
      ]);

      toastSuccess(t('toasts.customerUpdated'));
      navigate(`/org/${orgSlug}/customers/${id}`);
    } catch (err: unknown) {
      console.error('[EditCustomerPage] save error:', err);
      const message = err && typeof err === 'object' && 'message' in err
        ? String((err as { message: unknown }).message)
        : t('form.saveChangesError');
      setSubmitError(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-green-50">
      <Navbar displayName={user?.displayName ?? null} onSignOut={signOut} onLogoClick={() => navigate('/')} onOrgNameClick={() => navigate(`/org/${orgSlug}`)} onAvatarClick={() => navigate("/profile")} orgName={org.name} />

      <main className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
        <BackButton />

        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : notFound ? (
          <div className="py-16 text-center">
            <p className="font-display text-base font-semibold text-gray-700">{t('customers.notFound')}</p>
            <p className="mt-1 font-body text-sm text-gray-400">{t('customers.notFoundDetail')}</p>
          </div>
        ) : (
          <>
            {/* Page heading */}
            <div className="mb-5">
              <h1 className="font-display text-xl font-semibold text-gray-900">{t('form.editTitle')}</h1>
              <p className="mt-1 font-body text-sm text-gray-500">
                {t('form.editSubtitle')}
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <Card variant="form">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input
                    name="firstName"
                    label={t('form.firstName')}
                    required
                    placeholder={t('form.firstNamePlaceholder')}
                    value={fields.firstName}
                    onChange={handleChange}
                    error={errors.firstName}
                    autoComplete="given-name"
                  />
                  <Input
                    name="lastName"
                    label={t('form.lastName')}
                    required
                    placeholder={t('form.lastNamePlaceholder')}
                    value={fields.lastName}
                    onChange={handleChange}
                    error={errors.lastName}
                    autoComplete="family-name"
                  />
                  <Input
                    name="phone"
                    label={t('form.phone')}
                    required
                    type="tel"
                    placeholder={t('form.phonePlaceholder')}
                    value={fields.phone}
                    onChange={handleChange}
                    error={errors.phone}
                    autoComplete="tel"
                  />
                  <Input
                    name="address"
                    label={t('form.address')}
                    required
                    placeholder={t('form.addressPlaceholder')}
                    value={fields.address}
                    onChange={handleChange}
                    error={errors.address}
                    autoComplete="street-address"
                  />
                  <Input
                    name="email"
                    label={t('form.email')}
                    type="email"
                    placeholder={t('form.emailPlaceholder')}
                    value={fields.email}
                    onChange={handleChange}
                    error={errors.email}
                    autoComplete="email"
                  />
                  <Input
                    name="registrationDate"
                    label={t('form.registrationDate')}
                    type="date"
                    value={fields.registrationDate}
                    onChange={handleChange}
                  />
                </div>

                <div className="mt-4">
                  <CustomerCropsTable
                    value={cropRows}
                    cropTypes={cropTypes}
                    regions={regions}
                    onChange={handleCropChange}
                    error={cropError}
                    disabled={saving}
                  />
                </div>
              </Card>

              {submitError && (
                <div className="mt-4 rounded bg-red-50 px-4 py-3 font-body text-sm text-red-600">
                  {submitError}
                </div>
              )}

              <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate(`/org/${orgSlug}/customers/${id}`)}
                  disabled={saving}
                >
                  {t('form.cancel')}
                </Button>
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size="sm" />
                      {t('form.saving')}
                    </>
                  ) : (
                    t('form.saveChanges')
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
